import React, { useEffect, useMemo } from "react";
import { useSessionTracking } from "@/hooks/useSessionTracking";
import { useLikes } from "@/hooks/useLikes";
import { useCommentCount } from "@/hooks/useCommentCount";
import { useViewCount } from "@/hooks/useViewCount";
import { usePostActivity } from "@/hooks/usePostActivity";
import { Heart, MessagesSquare } from "lucide-react";
import type { PostMetrics } from "@/hooks/usePostMetrics";
import BlogPostSocialLinks from "./BlogPostSocialLinks";

interface BlogPost {
  id: string;
  title: string;
  content: string;
  preview: string;
  image_url?: string;
  facebook_url?: string;
  twitter_url?: string;
  instagram_url?: string;
  linkedin_url?: string;
  youtube_url?: string;
  tiktok_url?: string;
  threads_url?: string;
  google_business_url?: string;
  pinterest_url?: string;
  snapchat_url?: string;
  bluesky_url?: string;
  reddit_url?: string;
  substack_url?: string;
  discord_url?: string;
  published: boolean;
  published_at?: string;
  created_at: string;
  user_id: string;
}

interface PostLikesProps {
  postId: string;
  content: string;
  post: BlogPost;
  isDetailView?: boolean;
  /** Pre-fetched metrics for list view (avoids individual fetches) */
  metrics?: PostMetrics;
  onPostClick?: (post: BlogPost) => void;
  onHasNewActivity?: (hasNewActivity: boolean) => void;
  getSocialIcon: (platform: string) => string | React.ComponentType<{ className?: string }> | null;
  /** Compact mode shows icons-only counts for list view */
  compactMode?: boolean;
}

const PostLikes: React.FC<PostLikesProps> = ({
  postId,
  content,
  post,
  isDetailView,
  metrics,
  onPostClick,
  onHasNewActivity,
  getSocialIcon,
  compactMode = false,
}) => {
  const { sessionKey } = useSessionTracking();
  
  // In detail view, use individual hooks for real-time updates
  // In list view with pre-fetched metrics, skip individual fetches
  const shouldUseHooks = isDetailView || !metrics;
  
  const likesHook = useLikes(shouldUseHooks ? postId : '', "post");
  const commentCountHook = useCommentCount(shouldUseHooks ? postId : '');
  const viewCountHook = useViewCount(shouldUseHooks ? postId : '');
  const activityHook = usePostActivity(shouldUseHooks ? postId : '');

  // Use pre-fetched metrics in list view, hooks in detail view
  const likeCount = shouldUseHooks ? likesHook.likeCount : (metrics?.likeCount ?? 0);
  const isLiked = shouldUseHooks ? likesHook.isLiked : (metrics?.isLiked ?? false);
  const commentCount = shouldUseHooks ? commentCountHook.commentCount : (metrics?.commentCount ?? 0);
  const viewCount = shouldUseHooks ? viewCountHook.viewCount : (metrics?.viewCount ?? 0);
  const hasNewActivity = shouldUseHooks ? activityHook.hasNewActivity : (metrics?.hasNewActivity ?? false);
  const newCommentCount = shouldUseHooks ? activityHook.newCommentCount : (metrics?.newCommentCount ?? 0);

  // For toggling likes, always use the hook's toggleLike
  const { toggleLike } = likesHook;

  // Report activity status to parent
  useEffect(() => {
    onHasNewActivity?.(hasNewActivity);
  }, [hasNewActivity, onHasNewActivity]);

  // Calculate word count by stripping HTML and counting words
  const wordCount = useMemo(() => {
    const text = content
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return text.split(" ").filter((word) => word.length > 0).length;
  }, [content]);

  const handleCommentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDetailView) {
      // In detail view, just scroll to comments
      const commentsSection = document.getElementById("comments-section");
      commentsSection?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (onPostClick) {
      // In list view, navigate to post and then scroll to comments
      onPostClick(post);
      setTimeout(() => {
        const commentsSection = document.getElementById("comments-section");
        commentsSection?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  };

  const handleLikeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleLike(sessionKey);
  };

  // Compact mode: icons-only counts for list view
  if (compactMode) {
    return (
      <div className="mt-2 space-y-2">
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <button
            onClick={handleLikeClick}
            className={`flex items-center gap-1 hover:text-red-500 transition-colors ${
              isLiked ? "text-red-500" : "text-muted-foreground"
            }`}
          >
            <Heart className={`w-4 h-4 ${isLiked ? "fill-current" : ""}`} />
            <span>{likeCount}</span>
          </button>
          <span className="flex items-center gap-1">
            👁️ {viewCount}
          </span>
          <button
            onClick={handleCommentClick}
            className="flex items-center gap-1 hover:text-primary transition-colors cursor-pointer"
          >
            <MessagesSquare className="w-4 h-4" />
            <span>{commentCount}</span>
            {hasNewActivity && newCommentCount > 0 && (
              <span className="text-blue-500 font-medium">+{newCommentCount}</span>
            )}
          </button>
          <span className="flex items-center gap-1">
            📝 {wordCount}
          </span>
        </div>
        <BlogPostSocialLinks post={post} getSocialIcon={getSocialIcon} />
      </div>
    );
  }

  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <button
          onClick={handleLikeClick}
          className={`flex items-center gap-1 hover:text-red-500 transition-colors ${
            isLiked ? "text-red-500" : "text-muted-foreground"
          }`}
        >
          <Heart className={`w-4 h-4 ${isLiked ? "fill-current" : ""}`} />
          <span>
            {likeCount} {likeCount === 1 ? "like" : "likes"}
          </span>
        </button>
        <span>
          👁️ {viewCount} {viewCount === 1 ? "view" : "views"}
        </span>
        <button
          onClick={handleCommentClick}
          className="flex items-center gap-1.5 hover:text-primary transition-colors cursor-pointer"
        >
          <MessagesSquare className="w-4 h-4" />
          {commentCount} {commentCount === 1 ? "comment" : "comments"}
          {hasNewActivity && newCommentCount > 0 && (
            <span className="text-blue-500 font-medium">({newCommentCount} new)</span>
          )}
        </button>
        <span>
          📝 {wordCount} {wordCount === 1 ? "word" : "words"}
        </span>
      </div>

      <BlogPostSocialLinks post={post} getSocialIcon={getSocialIcon} />
    </div>
  );
};

export default PostLikes;
